import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "@/shared/ui";
import {
  MagnifyingGlassIcon,
  DocumentTextIcon,
  BriefcaseIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { analytics } from "../utils/analytics";
import { getUserDocuments } from "@/shared/db";

import type { SearchResult } from "../shared/types";

export const GlobalSearch: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Keyboard shortcut (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Search when term changes
  useEffect(() => {
    if (!searchTerm.trim() || !user) {
      setResults([]);
      return;
    }

    const search = async () => {
      setLoading(true);
      try {
        const searchResults: SearchResult[] = [];

        // Search documents
        if (
          profile?.user_type === "lawyer" ||
          profile?.user_type === "client"
        ) {
          try {
            const documents = await getUserDocuments(user.id);
            const matchingDocs = documents
              .filter(
                (doc) =>
                  doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  doc.content?.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .slice(0, 5)
              .map((doc) => ({
                id: doc.id,
                type: "document" as const,
                title: doc.title,
                description: doc.document_type,
                url: `/dashboard/${profile.user_type}/documents/${doc.id}`,
              }));
            searchResults.push(...matchingDocs);
          } catch (error) {
            console.error("Error searching documents:", error);
          }
        }

        setResults(searchResults);
        analytics.track("global_search", {
          query: searchTerm,
          results_count: searchResults.length,
        });
      } catch (error) {
        console.error("Error performing search:", error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(search, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, user, profile]);

  const handleResultClick = (result: SearchResult) => {
    analytics.track("global_search_result_clicked", {
      result_type: result.type,
      result_id: result.id,
    });
    navigate(result.url);
    setIsOpen(false);
    setSearchTerm("");
  };

  const getResultIcon = (type: SearchResult["type"]) => {
    switch (type) {
      case "document":
        return DocumentTextIcon;
      case "case":
        return BriefcaseIcon;
      default:
        return DocumentTextIcon;
    }
  };

  return (
    <>
      {/* Search Button/Trigger */}
      <button
        onClick={() => setIsOpen(true)}
        className={`flex items-center gap-2 text-sm text-tiber-600 bg-white border border-tiber-600 rounded-lg hover:bg-romance-50 transition-colors ${compact ? "p-2 min-w-0" : "px-4 py-2"}`}
        title={t("search.shortcut")}
      >
        <MagnifyingGlassIcon className="h-4 w-4" />
        <span className="hidden sm:inline">{t("search.placeholder")}</span>
        <kbd className="hidden sm:inline-flex items-center px-2 py-1 text-xs font-semibold text-tiber-600 bg-romance-50 border border-tiber-200 rounded">
          Ctrl K
        </kbd>
      </button>

      {/* Search Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={() => setIsOpen(false)}
            />

            {/* Search Panel */}
            <motion.div
              ref={searchRef}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="fixed top-16 sm:top-20 left-2 right-2 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 w-auto sm:w-full max-w-2xl bg-white rounded-lg shadow-xl z-50 border border-tiber-600 mx-0 sm:mx-0"
            >
              <div className="p-4 border-b border-tiber-600">
                <div className="flex items-center gap-2">
                  <Input
                    autoFocus
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t(
                      "search.placeholder",
                      "Search documents, cases..."
                    )}
                    leftIcon={
                      <MagnifyingGlassIcon className="h-5 w-5 text-tiber-800" />
                    }
                    rightIcon={
                      <XMarkIcon
                        className="h-5 w-5 text-tiber-800 cursor-pointer"
                        onClick={() => setIsOpen(false)}
                      />
                    }
                    onClick={() => setIsOpen(false)}
                  />
                </div>
              </div>

              {/* Results */}
              <div className="max-h-96 overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-tiber-800">
                    {t("search.loading")}
                  </div>
                ) : results.length > 0 ? (
                  <div className="divide-y divide-tiber-600">
                    {results.map((result) => {
                      const Icon = getResultIcon(result.type);
                      return (
                        <button
                          key={`${result.type}-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          className="w-full p-4 text-left hover:bg-romance-50 transition-colors"
                        >
                          <div className="flex items-start gap-3">
                            <Icon className="h-5 w-5 text-tiber-800 mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-tiber-900 truncate">
                                {result.title}
                              </div>
                              {result.description && (
                                <div className="text-sm text-tiber-800 mt-1">
                                  {result.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : searchTerm.trim() ? (
                  <div className="p-8 text-center text-tiber-800">
                    {t("search.noResults")}
                  </div>
                ) : (
                  <div className="p-8 text-center text-tiber-800">
                    {t("search.startTyping")}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
